import React from 'react';
import { Sliders, Calculator, TrendingUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating,
  cryptocurrencyTypes
}) => {
  // Разбор текущих значений
  const initialInvestment = parseFloat(formData.initialInvestment) || 0;
  const investmentPeriod = parseInt(formData.investmentPeriod) || 0;
  const expectedGrowthRate = parseFloat(formData.expectedGrowthRate) || 0;
  
  // Обработчики изменения значений слайдеров
  const handleInitialInvestmentChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      initialInvestment: newValue
    }));
    
    // Очистка ошибки, если она есть
    if (errors.initialInvestment) {
      delete errors.initialInvestment;
    }
  };
  
  const handleInvestmentPeriodChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      investmentPeriod: newValue
    }));
    
    // Очистка ошибки, если она есть
    if (errors.investmentPeriod) {
      delete errors.investmentPeriod;
    }
  };
  
  const handleExpectedGrowthRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      expectedGrowthRate: newValue
    }));
    
    // Очистка ошибки, если она есть
    if (errors.expectedGrowthRate) {
      delete errors.expectedGrowthRate;
    }
  };
  
  // Предопределенные суммы инвестиций
  const predefinedInvestments = [
    { value: '10000', label: '10 тыс.' },
    { value: '100000', label: '100 тыс.' },
    { value: '500000', label: '500 тыс.' },
    { value: '1000000', label: '1 млн.' }
  ];
  
  // Предопределенные сроки инвестирования
  const predefinedPeriods = [
    { value: '3', label: '3 мес.' },
    { value: '6', label: '6 мес.' },
    { value: '12', label: '1 год' },
    { value: '36', label: '3 года' }
  ];
  
  // Предопределенные ставки роста
  const predefinedRates = [
    { value: '5', label: '5%' },
    { value: '10', label: '10%' },
    { value: '15', label: '15%' },
    { value: '20', label: '20%' }
  ];
  
  // Функция расчета
  const calculateInvestment = () => {
    // Имитация клика по кнопке расчета в основной форме
    document.querySelector('button[type="submit"]')?.click();
  };
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Sliders className="w-5 h-5 mr-2 text-indigo-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Сумма инвестиции */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="initialInvestmentSlider" className="text-gray-600 dark:text-gray-400">Сумма инвестиции</label>
            <span className="font-medium text-gray-900 dark:text-white">{initialInvestment.toLocaleString('ru-RU')} ₽</span>
          </div>
          <input
            type="range"
            id="initialInvestmentSlider"
            min="1000"
            max="5000000"
            step="1000"
            value={initialInvestment}
            onChange={handleInitialInvestmentChange}
            aria-label="Сумма инвестиции"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Кнопки с предопределенными суммами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedPeriods.map(period => (
              <button
                key={period.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, investmentPeriod: period.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.investmentPeriod === period.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {period.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Ожидаемый месячный рост */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="expectedGrowthRateSlider" className="text-gray-600 dark:text-gray-400">Ожидаемый рост в месяц</label>
            <span className="font-medium text-gray-900 dark:text-white">{expectedGrowthRate}%</span>
          </div>
          <input
            type="range"
            id="expectedGrowthRateSlider"
            min="-20"
            max="50"
            step="0.5"
            value={expectedGrowthRate}
            onChange={handleExpectedGrowthRateChange}
            aria-label="Ожидаемый рост в месяц (%)"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-indigo-600"
          />
          
          {/* Предопределенные процентные ставки */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedRates.map(rate => (
              <button
                key={rate.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, expectedGrowthRate: rate.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.expectedGrowthRate === rate.value 
                    ? 'bg-indigo-100 border-indigo-300 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {rate.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Выбор криптовалюты */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="quickCryptoSelect" className="text-gray-600 dark:text-gray-400">Криптовалюта</label>
          </div>
          <select
            id="quickCryptoSelect"
            value={formData.cryptocurrencyType}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              cryptocurrencyType: e.target.value,
              expectedGrowthRate: cryptocurrencyTypes.find(c => c.id === e.target.value)?.defaultMonthlyGrowth.toString() || prev.expectedGrowthRate
            }))}
            className="form-select w-full mb-2"
          >
            {cryptocurrencyTypes.map(crypto => (
              <option key={crypto.id} value={crypto.id}>
                {crypto.name}
              </option>
            ))}
          </select>
        </div>
        
        {/* Дополнительные чекбоксы */}
        <div className="space-y-2">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.reinvestProfit}
              onChange={(e) => setFormData(prev => ({ ...prev, reinvestProfit: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Реинвестировать прибыль
            </span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.additionalInvestments}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalInvestments: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Регулярные инвестиции
            </span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={formData.scenarioAnalysis}
              onChange={(e) => setFormData(prev => ({ ...prev, scenarioAnalysis: e.target.checked }))}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Сценарный анализ
            </span>
          </label>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateInvestment}
          icon={<TrendingUp className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать
        </Button>
      </div>
    </div>
  );
};

// Вспомогательная функция для склонения слов
const pluralize = (number, one, two, five) => {
  let n = Math.abs(number);
  n %= 100;
  if (n >= 5 && n <= 20) {
    return five;
  }
  n %= 10;
  if (n === 1) {
    return one;
  }
  if (n >= 2 && n <= 4) {
    return two;
  }
  return five;
};

export default QuickAdjustmentSidebar;